/**
 * NumberSeriesPlaceholderHints Component
 *
 * Lists the placeholders a pattern may use, straight from the shared placeholder list.
 */
import { NUMBER_SERIES_PLACEHOLDERS, type NumberSeriesPlaceholder } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface NumberSeriesPlaceholderHintsProps {
  /** Inserts the clicked placeholder into the pattern being edited. */
  onInsert: (placeholder: NumberSeriesPlaceholder) => void;
}

export function NumberSeriesPlaceholderHints({ onInsert }: NumberSeriesPlaceholderHintsProps) {
  const t = useFeatureTranslation('systemSettings.numberSeries');

  return (
    <div className='rounded-lg bg-background-muted p-md' data-testid='number-series-placeholders'>
      <p className='text-sm font-medium text-foreground-base'>{t('placeholders.title')}</p>
      <dl className='mt-sm grid grid-cols-1 sm:grid-cols-2 gap-sm'>
        {NUMBER_SERIES_PLACEHOLDERS.map(placeholder => (
          <div key={placeholder} className='flex items-baseline gap-sm'>
            <dt>
              <button
                type='button'
                data-testid={`number-series-placeholder-${placeholder}`}
                title={t('placeholders.insert')}
                aria-label={t('placeholders.insert')}
                className='text-xs bg-background-input px-sm py-xs rounded font-mono text-foreground-base border border-transparent cursor-pointer hover:border-primary-base hover:text-primary-base focus-visible:outline-2 focus-visible:outline-primary-base'
                onClick={() => onInsert(placeholder)}
              >
                {`{${placeholder}}`}
              </button>
            </dt>
            <dd className='text-xs text-foreground-muted'>{t(`placeholders.${placeholder}`)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
